import { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface QuizActivityDetails {
  currentQuestion: {
    id: string;
    text: string;
    options: Array<{
      id: string;
      text: string;
      is_correct: boolean;
    }>;
    explanation?: string;
  } | null;
  totalQuestions: number;
  currentQuestionIndex: number;
  selectedAnswers: { [questionId: string]: string };
  timeElapsed: number;
  subject: string;
  category?: string;
  difficulty?: string;
}

interface WorksheetActivityDetails {
  title: string;
  type: 'matching' | 'sorting' | 'puzzle' | 'general';
  content: any;
  currentStep: number;
  totalSteps: number;
  completedSteps: string[];
  subject: string;
  grade: string;
}

interface GameActivityDetails {
  gameId: string;
  gameName: string;
  gameType: string;
  currentLevel: number;
  score: number;
  timeElapsed: number;
  gameState: any;
  subject: string;
  difficulty: number;
}

interface WritingActivityDetails {
  topic: string;
  prompt: string;
  currentContent: string;
  wordCount: number;
  suggestions: string[];
  timeElapsed: number;
  grade: string;
}

interface ActivityContextData {
  currentActivity: 'test' | 'game' | 'worksheet' | 'writing' | 'dashboard' | 'subject_selection' | 'analysis' | null;
  currentPage: string;
  isActive: boolean;
  quizDetails: QuizActivityDetails | null;
  worksheetDetails: WorksheetActivityDetails | null;
  gameDetails: GameActivityDetails | null;
  writingDetails: WritingActivityDetails | null;
  navigationHistory: string[];
  sessionStartTime: number;
  totalTimeElapsed: number;
  userContext: {
    grade?: string;
    subject?: string;
    selectedGrade?: string;
    selectedSubject?: string;
    recentActivity?: any[];
    learningPreferences?: any;
  };
  progress: number;
  completionStatus: 'not_started' | 'in_progress' | 'completed' | 'paused';
}

interface ActivityContextType {
  activityContext: ActivityContextData;
  updateActivityContext: (data: Partial<ActivityContextData>) => void;
  updateQuizDetails: (details: Partial<QuizActivityDetails>) => void;
  updateWorksheetDetails: (details: Partial<WorksheetActivityDetails>) => void;
  updateGameDetails: (details: Partial<GameActivityDetails>) => void;
  updateWritingDetails: (details: Partial<WritingActivityDetails>) => void;
  updateUserContext: (context: Partial<ActivityContextData['userContext']>) => void;
  getCurrentActivityData: () => any;
  addToNavigationHistory: (path: string) => void;
  resetActivityContext: () => void;
  getContextForChatbot: () => any;
}

const ActivityContext = createContext<ActivityContextType | null>(null);

export const ActivityProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [activityContext, setActivityContext] = useState<ActivityContextData>({
    currentActivity: null,
    currentPage: '',
    isActive: false,
    quizDetails: null,
    worksheetDetails: null,
    gameDetails: null,
    writingDetails: null,
    navigationHistory: [],
    sessionStartTime: Date.now(),
    totalTimeElapsed: 0,
    userContext: {},
    progress: 0,
    completionStatus: 'not_started'
  });

  const addToNavigationHistory = (path: string) => {
    setActivityContext(prev => ({
      ...prev,
      navigationHistory: [...prev.navigationHistory.slice(-9), path]
    }));
  };

  useEffect(() => {
    const path = location.pathname;
    let currentActivity: ActivityContextData['currentActivity'] = null;
    let currentPage = '';
    
    // Treat both /test/... and /grade/:grade/subject/:subject as active test pages
    const isTestRoute = path.includes('/test/') || (path.includes('/grade/') && path.includes('/subject/'));

    if (isTestRoute) {
      currentActivity = 'test';
      currentPage = 'Test Page';
    } else if (path.includes('/games/')) {
      currentActivity = 'game';
      currentPage = 'Games Page';
    } else if (path.includes('/worksheets/')) {
      currentActivity = 'worksheet';
      currentPage = 'Worksheets Page';
    } else if (path.includes('/writing/')) {
      currentActivity = 'writing';
      currentPage = 'Writing Practice Page';
    } else if (path.includes('/analysis/')) {
      currentActivity = 'analysis';
      currentPage = 'Performance Analysis Page';
    } else if (path.includes('/grade/')) {
      currentActivity = 'subject_selection';
      currentPage = 'Subject Selection Page';
    } else if (path === '/dashboard') {
      currentActivity = 'dashboard';
      currentPage = 'Dashboard';
    } else {
      currentPage = 'Home Page';
    }

    setActivityContext(prev => ({
      ...prev,
      currentActivity,
      currentPage,
      isActive: currentActivity !== null,
      completionStatus: currentActivity ? 'in_progress' : 'not_started'
    }));

    addToNavigationHistory(path);
  }, [location]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (activityContext.isActive) {
      intervalId = setInterval(() => {
        setActivityContext(prev => ({
          ...prev,
          totalTimeElapsed: Date.now() - prev.sessionStartTime
        }));
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [activityContext.isActive]);

  const updateActivityContext = (data: Partial<ActivityContextData>) => {
    setActivityContext(prev => ({ ...prev, ...data }));
  };

  const updateQuizDetails = (details: Partial<QuizActivityDetails>) => {
    setActivityContext(prev => ({
      ...prev,
      quizDetails: prev.quizDetails ? { ...prev.quizDetails, ...details } : details as QuizActivityDetails
    }));
  };

  const updateWorksheetDetails = (details: Partial<WorksheetActivityDetails>) => {
    setActivityContext(prev => ({
      ...prev,
      worksheetDetails: prev.worksheetDetails ? { ...prev.worksheetDetails, ...details } : details as WorksheetActivityDetails
    }));
  };

  const updateGameDetails = (details: Partial<GameActivityDetails>) => {
    setActivityContext(prev => ({
      ...prev,
      gameDetails: prev.gameDetails ? { ...prev.gameDetails, ...details } : details as GameActivityDetails
    }));
  };

  const updateWritingDetails = (details: Partial<WritingActivityDetails>) => {
    setActivityContext(prev => ({
      ...prev,
      writingDetails: prev.writingDetails ? { ...prev.writingDetails, ...details } : details as WritingActivityDetails
    }));
  };

  const updateUserContext = (context: Partial<ActivityContextData['userContext']>) => {
    setActivityContext(prev => ({
      ...prev,
      userContext: { ...prev.userContext, ...context }
    }));
  };

  const resetActivityContext = () => {
    setActivityContext(prev => ({
      ...prev,
      quizDetails: null,
      worksheetDetails: null,
      gameDetails: null,
      writingDetails: null,
      progress: 0,
      completionStatus: 'not_started',
      sessionStartTime: Date.now(),
      totalTimeElapsed: 0
    }));
  };

  const getCurrentActivityData = () => {
    return {
      activity: activityContext.currentActivity,
      route: location.pathname,
      currentPage: activityContext.currentPage,
      isActive: activityContext.isActive,
      progress: activityContext.progress,
      completionStatus: activityContext.completionStatus,
      timeElapsed: activityContext.totalTimeElapsed,
      navigationHistory: activityContext.navigationHistory,
      userContext: activityContext.userContext,
      activityDetails: {
        quiz: activityContext.quizDetails,
        worksheet: activityContext.worksheetDetails,
        game: activityContext.gameDetails,
        writing: activityContext.writingDetails
      }
    };
  };

  const getContextForChatbot = () => {
    const context: any = {
      currentActivity: activityContext.currentActivity,
      currentPage: activityContext.currentPage,
      userContext: activityContext.userContext,
      timeElapsed: Math.floor(activityContext.totalTimeElapsed / 1000),
      progress: activityContext.progress
    };

    switch (activityContext.currentActivity) {
      case 'test':
        if (activityContext.quizDetails) {
          context.currentQuestion = activityContext.quizDetails.currentQuestion;
          context.quizProgress = {
            current: activityContext.quizDetails.currentQuestionIndex + 1,
            total: activityContext.quizDetails.totalQuestions,
            subject: activityContext.quizDetails.subject,
            category: activityContext.quizDetails.category
          };
        }
        break;
      
      case 'game':
        if (activityContext.gameDetails) {
          context.gameInfo = {
            name: activityContext.gameDetails.gameName,
            type: activityContext.gameDetails.gameType,
            level: activityContext.gameDetails.currentLevel,
            score: activityContext.gameDetails.score,
            subject: activityContext.gameDetails.subject
          };
        }
        break;
      
      case 'worksheet':
        if (activityContext.worksheetDetails) {
          context.worksheetInfo = {
            title: activityContext.worksheetDetails.title,
            type: activityContext.worksheetDetails.type,
            progress: {
              current: activityContext.worksheetDetails.currentStep,
              total: activityContext.worksheetDetails.totalSteps
            },
            subject: activityContext.worksheetDetails.subject
          };
        }
        break;
      
      case 'writing':
        if (activityContext.writingDetails) {
          context.writingInfo = {
            topic: activityContext.writingDetails.topic,
            prompt: activityContext.writingDetails.prompt,
            wordCount: activityContext.writingDetails.wordCount,
            suggestions: activityContext.writingDetails.suggestions
          };
        }
        break;
    }

    return context;
  };

  const contextValue: ActivityContextType = {
    activityContext,
    updateActivityContext,
    updateQuizDetails,
    updateWorksheetDetails,
    updateGameDetails,
    updateWritingDetails,
    updateUserContext,
    getCurrentActivityData,
    addToNavigationHistory,
    resetActivityContext,
    getContextForChatbot
  };

  return (
    <ActivityContext.Provider value={contextValue}>
      {children}
    </ActivityContext.Provider>
  );
};

export const useActivityContext = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivityContext must be used within ActivityProvider');
  }
  return context;
};